import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';

const AuthContext = createContext();

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loadingAuth, setLoadingAuth] = useState(true);
    const [orgName, setOrgName] = useState(null);
    const [licenseId, setLicenseId] = useState(null);
    const [history, setHistory] = useState([]);
    const [profileData, setProfileData] = useState(null);

    // Derived state for the Paywall logic
    const isPremium = !!licenseId; 

    const fetchUserData = async (currentUser) => {
        try {
            const token = await currentUser.getIdToken();
            
            // 1. Sync License
            const syncRes = await fetch('/api/auth/google-sync', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const syncData = await syncRes.json();
            
            if (syncData.organisation) setOrgName(syncData.organisation);
            setLicenseId(syncData.licenseId || null);

            // 2. Fetch History & Profile
            const dataRes = await fetch('/api/user/data', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const userData = await dataRes.json();
            
            if (userData.history && Array.isArray(userData.history)) {
                setHistory(userData.history.sort((a, b) => new Date(b.date) - new Date(a.date)));
            }
            setProfileData(userData.profile);

        } catch (e) {
            console.error("Global Auth Sync Failed:", e);
        }
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            setUser(currentUser);
            if (currentUser) {
                await fetchUserData(currentUser);
            } else {
                setOrgName(null);
                setLicenseId(null);
                setHistory([]);
                setProfileData(null);
            }
            setLoadingAuth(false);
        });
        return () => unsubscribe();
    }, []);

    // We pass a function down so components can trigger a manual data refresh
    const refreshData = async () => {
        if (user) await fetchUserData(user);
    };

    const value = {
        user,
        loadingAuth,
        orgName,
        licenseId,
        isPremium,
        history,
        profileData,
        refreshData
    };

    return (
        <AuthContext.Provider value={value}>
            {!loadingAuth && children}
        </AuthContext.Provider>
    );
}